import { Suspense, useEffect } from 'react';
import { Routes, Route } from 'react-router-dom';
import { ErrorBoundary } from '@bloomcode/error-handling';
import { LoadingSpinner, Toaster } from '@bloomcode/ui';
import { getAuthPortalLoginUrl, getAuthPortalSignupUrl, authPortalUrl } from './config/env';

// Layout
import MainLayout from './layouts/MainLayout';

// Pages
import HomePage from './pages/HomePage';
import NotFoundPage from './pages/NotFoundPage';
import DashboardPage from './pages/dashboard/DashboardPage';
import InvoicesPage from './pages/invoices/InvoicesPage';
import InvoiceDetailPage from './pages/invoices/InvoiceDetailPage';
import StatementsPage from './pages/statements/StatementsPage';
import PaymentsPage from './pages/payments/PaymentsPage';

function AccountPortalRedirect() {
  useEffect(() => {
    const url =
      import.meta.env?.VITE_ACCOUNT_PORTAL_URL || 'https://account.staging.bloomcodetechnologies.online';
    window.location.href = `${url}/profile`;
  }, []);
  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="text-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4" />
        <p className="text-muted-foreground">Redirecting to account settings...</p>
      </div>
    </div>
  );
}

function AuthPortalRedirect() {
  useEffect(() => {
    window.location.href = getAuthPortalLoginUrl(window.location.origin + '/dashboard');
  }, []);
  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="text-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4" />
        <p className="text-muted-foreground">Redirecting to login...</p>
      </div>
    </div>
  );
}

function AuthPortalSignupRedirect() {
  useEffect(() => {
    window.location.href = getAuthPortalSignupUrl(
      window.location.origin + '/dashboard',
      'Bloomcode Ledger Portal'
    );
  }, []);
  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="text-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4" />
        <p className="text-muted-foreground">Redirecting to signup...</p>
      </div>
    </div>
  );
}

function AuthPortalTokenRedirect({ portalPath }: { portalPath: string }) {
  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const returnUrl = window.location.origin + '/dashboard';
    const url = new URL(portalPath, authPortalUrl);
    searchParams.forEach((value, key) => {
      url.searchParams.set(key, value);
    });
    if (!url.searchParams.has('returnUrl')) {
      url.searchParams.set('returnUrl', returnUrl);
    }
    window.location.href = url.toString();
  }, [portalPath]);
  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="text-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4" />
        <p className="text-muted-foreground">Redirecting...</p>
      </div>
    </div>
  );
}

export function App() {
  return (
    <ErrorBoundary>
      <Suspense fallback={<LoadingSpinner className="h-screen" />}>
        <Routes>
          {/* Public */}
          <Route element={<MainLayout />}>
            <Route index element={<HomePage />} />
          </Route>

          {/* Auth redirects */}
          <Route path="/auth/login" element={<AuthPortalRedirect />} />
          <Route path="/auth/register" element={<AuthPortalSignupRedirect />} />
          <Route
            path="/auth/accept-invitation"
            element={<AuthPortalTokenRedirect portalPath="/accept-invitation" />}
          />

          {/* Protected routes */}
          <Route path="/dashboard" element={<MainLayout requireAuth />}>
            <Route index element={<DashboardPage />} />
          </Route>

          <Route path="/invoices" element={<MainLayout requireAuth />}>
            <Route index element={<InvoicesPage />} />
            <Route path=":id" element={<InvoiceDetailPage />} />
          </Route>

          <Route path="/statements" element={<MainLayout requireAuth />}>
            <Route index element={<StatementsPage />} />
          </Route>

          <Route path="/payments" element={<MainLayout requireAuth />}>
            <Route index element={<PaymentsPage />} />
          </Route>

          {/* Profile → portal-account */}
          <Route path="/profile" element={<AccountPortalRedirect />} />

          {/* 404 */}
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </Suspense>
      <Toaster />
    </ErrorBoundary>
  );
}
